import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Edit, Trash, Plus, Settings } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { LanguageManagementModal } from '../modals/LanguageManagementModal';
import { useToast } from '../../context/ToastContext';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const LanguagesTab = () => {
    const { toast } = useToast();
    const { chargeLanguages, languageGroups, addChargeLanguage, updateChargeLanguage, deleteChargeLanguage, statuses, availableLanguages } = usePIM();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [editingLanguageId, setEditingLanguageId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    // Sorting
    const { items: sortedLanguages, requestSort, sortConfig } = useSortableData(chargeLanguages);

    const removeLanguageFromCharge = async (lang) => {
        console.log("Attempting to remove language:", lang);
        if (!deleteChargeLanguage) {
            console.error("deleteChargeLanguage is not defined in context");
            toast.error("Critical Error: deleteChargeLanguage function is missing from context. Please refresh the page.");
            return;
        }

        try {
            // Temporary removal of confirm dialog to debug issue
            console.log("Calling deleteChargeLanguage with ID:", lang.id);
            await deleteChargeLanguage(lang.id);
        } catch (error) {
            console.error("Error removing language:", error);
            toast.error(`Error: ${error.message}`);
        }
    };

    const toggleLanguageSelection = (langId) => {
        setSelectedLanguages(prev =>
            prev.includes(langId)
                ? prev.filter(id => id !== langId)
                : [...prev, langId]
        );
    };

    const handleAddSelected = async () => {
        for (const langId of selectedLanguages) {
            await addChargeLanguage(langId);
        }
        setIsAddModalOpen(false);
        setSelectedLanguages([]);
    };

    const startEditing = (lang) => {
        setEditingLanguageId(lang.id);
        setEditFormData({
            group_id: lang.effective_group_id || lang.group_id, // Use effective group ID (from charge or master)
            sw_status: lang.sw_status === 'Pending' ? '' : (lang.sw_status || ''),
            um_status: lang.um_status === 'Pending' ? '' : (lang.um_status || ''),
            qsg_status: lang.qsg_status === 'Pending' ? '' : (lang.qsg_status || ''),
            notes: lang.notes || ''
        });
    };

    const cancelEditing = () => {
        setEditingLanguageId(null);
        setEditFormData({});
    };

    const saveEditing = async (id) => {
        await updateChargeLanguage(id, editFormData);
        setEditingLanguageId(null);
        setEditFormData({});
    };

    const handleEditChange = (field, value) => {
        setEditFormData(prev => ({ ...prev, [field]: value }));
    };

    const getStatusColor = (statusName) => {
        const status = statuses.find(s => s.name === statusName);
        return status ? status.hex_code : 'stone';
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Languages</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage product translations and manuals.</p>
                </div>
                <div className="flex gap-3">
                    <GhostButton onClick={() => setIsAddModalOpen(true)}><Plus size={16} /> Add Language</GhostButton>
                    <GhostButton onClick={() => setIsManageModalOpen(true)}><Settings size={16} /> Manage Definitions</GhostButton>
                </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Language Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Code" sortKey="code" currentSort={sortConfig} onSort={requestSort} className="text-center w-16" />
                                <SortableHeader label="SW Status" sortKey="sw_status" currentSort={sortConfig} onSort={requestSort} className="w-[15%]" />
                                <SortableHeader label="UM Status" sortKey="um_status" currentSort={sortConfig} onSort={requestSort} className="w-[15%]" />
                                <SortableHeader label="QSG Status" sortKey="qsg_status" currentSort={sortConfig} onSort={requestSort} className="w-[15%]" />
                                <SortableHeader label="Notes" sortKey="notes" currentSort={sortConfig} onSort={requestSort} className="w-[15%]" />
                                <th className="bg-stone-950 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-stone-800 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedLanguages.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="px-4 py-8 text-center text-stone-500 italic">No languages assigned to this charge.</td>
                                </tr>
                            )}
                            {sortedLanguages.map(lang => {
                                const isEditing = editingLanguageId === lang.id;
                                return (
                                    <tr
                                        key={lang.id}
                                        className={`transition-colors border-b border-stone-800 last:border-0 ${isEditing ? 'bg-stone-800/50' : 'hover:bg-stone-800/30 cursor-pointer'}`}
                                        onClick={() => !isEditing && startEditing(lang)}
                                    >
                                        {/* Group */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <select
                                                    value={editFormData.group_id || ''}
                                                    onChange={(e) => handleEditChange('group_id', e.target.value)}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs rounded px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="">No Group</option>
                                                    {languageGroups.map(g => (
                                                        <option key={g.id} value={g.id}>{g.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                lang.group_name ? (
                                                    <Badge color={lang.group_color || 'gray'}>{lang.group_name}</Badge>
                                                ) : (
                                                    <div className="h-7" />
                                                )
                                            )}
                                        </td>

                                        {/* Language Name */}
                                        <td className="px-4 py-0.5 text-sm text-stone-300">{lang.name}</td>

                                        {/* Language Code */}
                                        <td className="px-4 py-0.5 text-sm text-stone-400 font-mono text-center">{lang.code}</td>

                                        {/* SW Status */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <select
                                                    value={editFormData.sw_status}
                                                    onChange={(e) => handleEditChange('sw_status', e.target.value)}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs rounded px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="">Select Status</option>
                                                    {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                                </select>
                                            ) : (
                                                (lang.sw_status && lang.sw_status !== 'Pending') ? <Badge color={getStatusColor(lang.sw_status)}>{lang.sw_status}</Badge> : <div className="h-7" />
                                            )}
                                        </td>

                                        {/* UM Status */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <select
                                                    value={editFormData.um_status}
                                                    onChange={(e) => handleEditChange('um_status', e.target.value)}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs rounded px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="">Select Status</option>
                                                    {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                                </select>
                                            ) : (
                                                (lang.um_status && lang.um_status !== 'Pending') ? <Badge color={getStatusColor(lang.um_status)}>{lang.um_status}</Badge> : <div className="h-7" />
                                            )}
                                        </td>

                                        {/* QSG Status */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <select
                                                    value={editFormData.qsg_status}
                                                    onChange={(e) => handleEditChange('qsg_status', e.target.value)}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs rounded px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <option value="">Select Status</option>
                                                    {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                                </select>
                                            ) : (
                                                (lang.qsg_status && lang.qsg_status !== 'Pending') ? <Badge color={getStatusColor(lang.qsg_status)}>{lang.qsg_status}</Badge> : <div className="h-7" />
                                            )}
                                        </td>

                                        {/* Notes */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <Input
                                                    value={editFormData.notes}
                                                    onChange={(e) => handleEditChange('notes', e.target.value)}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <span className="text-xs text-stone-400 truncate max-w-[150px] block" title={lang.notes}>{lang.notes}</span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td className="px-4 py-0.5 text-right">
                                            <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                                {isEditing ? (
                                                    <>
                                                        <button onClick={() => saveEditing(lang.id)} className="w-8 h-8 rounded border border-green-600 text-green-400 hover:bg-green-900/20 flex items-center justify-center transition">✓</button>
                                                        <button onClick={cancelEditing} className="w-8 h-8 rounded border border-red-600 text-red-400 hover:bg-red-900/20 flex items-center justify-center transition">✕</button>
                                                    </>
                                                ) : (
                                                    <button onClick={() => removeLanguageFromCharge(lang)} className="w-8 h-8 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"><Trash size={14} /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Modal - Smart Selection */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add Languages" maxWidth="max-w-4xl">
                <div className="space-y-4">
                    <Input placeholder="Search available languages..." className="bg-stone-800 border-stone-600" />
                    <div className="max-h-[60vh] overflow-y-auto space-y-1">
                        {availableLanguages.filter(al => !chargeLanguages.some(cl => cl.master_id === al.id)).map(al => (
                            <div
                                key={al.id}
                                onClick={() => toggleLanguageSelection(al.id)}
                                className={`flex items-center justify-between px-3 py-1 border rounded cursor-pointer transition select-none ${selectedLanguages.includes(al.id) ? 'bg-blue-900/20 border-blue-500/50' : 'bg-stone-800/30 border-stone-700 hover:bg-stone-800'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedLanguages.includes(al.id)}
                                        onChange={() => { }} // Handled by parent div click
                                        className="accent-stone-500 w-4 h-4 cursor-pointer"
                                    />
                                    <div>
                                        <div className="text-sm text-stone-200">{al.name}</div>
                                        <div className="text-[10px] text-stone-500 font-mono leading-none">{al.code}</div>
                                    </div>
                                </div>
                                <Badge color={al.group_color} size="sm">{al.group_name}</Badge>
                            </div>
                        ))}
                        {availableLanguages.filter(al => !chargeLanguages.some(cl => cl.master_id === al.id)).length === 0 && (
                            <div className="text-center text-stone-500 py-4">No new languages available.</div>
                        )}
                    </div>
                    <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                        <GhostButton onClick={() => setIsAddModalOpen(false)}>Cancel</GhostButton>
                        <PrimaryButton onClick={handleAddSelected}>Add Selected ({selectedLanguages.length})</PrimaryButton>
                    </div>
                </div>
            </Modal>

            {/* Management Modals */}
            <LanguageManagementModal
                isOpen={isManageModalOpen}
                onClose={() => setIsManageModalOpen(false)}
            />
        </div>
    );
};
